<template>
  <div>
    <div v-if="renderElement(element, tabIdx, rowIdx, colIdx)">
      <div
        class="form-builder-elements"
        :class="{ 'edit-mode': isEditable }"
        :id="idx"
        @click="isEditable && $emit('editElement', element)"
        @dragover="isEditable && $emit('dragover', $event, idx)"
      >
        <div
          class="form-builder-element-wrap"
          :class="{
            'workBuilder-element-width': !isFullWidth,
            'drop-zone top':
              activeDropZone < draggedElementIdx &&
              idx == activeDropZone &&
              isEditable,
            'drop-zone bottom':
              activeDropZone > draggedElementIdx &&
              idx == activeDropZone &&
              isEditable,
          }"
          @dragstart="$emit('drag', idx)"
          @dragend="isEditable && $emit('drop', $event)"
          :draggable="isElementDragabble(element)"
        >
          <ul class="edit-mode-tools" v-if="isEditable && !isSheetDatabaseType">
            <li>
              <i
                class="far fa-trash-alt me-1"
                @click="
                  ($event) => {
                    $event.stopPropagation();
                    $emit('removeElement', element);
                  }
                "
              ></i>
            </li>
            <li>
              <i
                class="far fa-clone"
                @click="
                  ($event) => {
                    $event.stopPropagation();
                    $emit('duplicateElement', element, idx);
                  }
                "
              ></i>
            </li>
          </ul>
          <div class="form-builder-element-attributes">
            <div class="input-attribute">
              <label
                class="element-label"
                :class="element.name"
                :for="element.label"
                v-if="elementNeedsLabel(element)"
              >
                <i
                  v-if="element.type === 'html'"
                  class="fa fa-palette"
                  style="margin-right: 10px"
                ></i>
                <span>{{ element.label }}</span>
              </label>
              <input
                class="form-input"
                v-if="elementNeedsInput(element)"
                :required="isRequired(element.attributes)"
                :disabled="isReadOnly"
                :placeholder="getPlaceholder(element)"
                :type="element.type"
                :name="element.name"
                :class="{
                  [element.name]: true,
                  highlighted: isEmptyFileInput(element.label),
                  card: hasCardAppearance(element),
                  oval: hasOvalAppearance(element),
                }"
                v-model="element.value"
              />
              <input
                class="form-input"
                :class="element.name"
                v-else-if="['number', 'range'].includes(element.type)"
                :required="isRequired(element.attributes)"
                :disabled="isReadOnly"
                :placeholder="getPlaceholder(element)"
                :type="element.type"
                :name="element.name"
                v-model.number="element.value"
                :min="element.attributes.min"
                :max="element.attributes.max"
                :step="element.attributes.step"
              />
              <number-component
                v-else-if="element.type === 'formatted_number'"
                :editableComponent="true"
                :styleClass="element.name"
                :maximumDecimalValues="Number(element.attributes.step || 3)"
                :passedNumberValue="element.value || 0"
                :disabledInput="!isContentEditable || isAddOnly"
                @onNumberInputChange="onNumberComponentInputChange"
                :requiredInput="isRequired(element.attributes)"
                :componentPlaceholder="getPlaceholder(element)"
                :name="element.name"
                :maxValue="Number(element.attributes.max) || Infinity"
                :minValue="Number(element.attributes.min) || -Infinity"
                @toggleValidationError="toggleErrorInForm"
              />

              <!-- File Picker  Start -->
              <div
                class="edit-item-name"
                :class="{
                  [element.name]: true,
                  'update-select-file':
                    element.type === 'filenew' &&
                    !element.value &&
                    isAddingNewItem,
                }"
                v-if="element.type === 'filenew'"
              >
                <FilePicker
                  @onFileSelected="onFileSelected($event, element)"
                  @onFileDelete="onFileDelete(element)"
                  :ref="`filePickerRef${element.id}`"
                  :isAllowedToAddFile="isAddingNewItem"
                  :isActionsButtonEnable="isAddingNewItem"
                  :isAddOnly="isAddOnly"
                  :value="element.value"
                />
              </div>
              <!-- File Picker End -->

              <!-- sheet input start -->
              <div class="edit-item-name" v-if="element.type === 'sheet'">
                <input
                  class="form-input"
                  type="text"
                  :value="getStringifySheetObject(element.value)"
                />
              </div>
              <!-- sheet input end -->

              <!-- Smart Message or Text area -->
              <textarea
                v-if="['smartmessage', 'textarea'].includes(element.type)"
                :disabled="isReadOnly"
                :name="element.name"
                :required="element.attributes.required"
                v-model="element.value"
                :class="element.name"
              ></textarea>

              <div
                v-if="element.type === 'html' && !isEditable"
                :class="element.name"
                v-html="element.value"
              ></div>
            </div>
            <!-- Togglelist start -->
            <p
              class="togglelist-label"
              v-if="
                element.type == 'togglelist' &&
                element.name.includes('start') &&
                isEditable
              "
            >
              Elemente unterhalb dieses Textes werden in der Klappliste
              angezeigt.
            </p>
            <p
              class="togglelist-label"
              v-if="
                element.type == 'togglelist' &&
                element.name.includes('end') &&
                isEditable
              "
            >
              Elemente unterhalb dieses Textes werden nicht mehr in der
              Klappliste angezeigt.
            </p>
            <!-- Togglelist end -->

            <!-- linktodb start -->

            <div
              class="me-2 link-to-db-wrapper-edit"
              v-if="isLinkViewOnly || isLinkinEdit"
            >
              <i class="fa fa-link"></i>
              <a>{{ element.name }}</a>
            </div>

            <div
              v-if="isLinkinAdd"
              class="me-2 link-to-db-wrapper link-to-db-dropdown"
            >
              <GeneralDropdown
                className="database-dropdown"
                @onDropdownOpen="getDropdownOptions"
              >
                <template v-slot:selectFilter>
                  <div @click.stop>
                    <i class="fa fa-link"></i>
                    <a :href="generateDatabaseLink()" target="_blank">{{
                      element.name
                    }}</a>
                  </div>
                </template>
                <template v-slot:optionData>
                  <b-dropdown-item
                    v-if="isDbItemListLoading"
                    class="loading-overlay-wrapper"
                  >
                    <div class="loading-overlay">
                      <div class="progress-icon"></div>
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-for="(item, index) in databaseItemsList"
                    :key="`${item.id}-${index}`"
                    @click="onDatabaseItemChange(item)"
                    v-show="!isDbItemListLoading"
                  >
                    {{ item.id }}
                  </b-dropdown-item>
                </template>
              </GeneralDropdown>
            </div>

            <!-- linktodb end -->

            <!-- Wizard start -->

            <div
              class="wizard-title"
              v-if="element.type == 'title' && element.defaultTabs"
              :class="element.name"
            >
              <h3>{{ element.label }}</h3>
            </div>

            <!-- Wizard end -->

            <!-- Wrapper start -->
            <p
              class="togglelist-label"
              v-if="
                element.type == 'wrapper' &&
                element.name.includes('start') &&
                isEditable
              "
            >
              Elemente unterhalb dieses Textes werden in der Wrapper
              eingewickelt.
            </p>
            <p
              class="togglelist-label"
              v-if="
                element.type == 'wrapper' &&
                element.name.includes('end') &&
                isEditable
              "
            >
              Elemente unterhalb dieses Textes werden nicht mehr in der Wrapper
              eingewickelt.
            </p>
            <!-- Wrapper end -->

            <img
              :src="element.url"
              v-if="element.url && element.type === 'image'"
              alt="image"
              class="preview-image"
              :class="element.name"
            />
            <span
              v-else-if="element.type === 'image' && element.url === ''"
              class="required-url"
              :class="element.name"
              >Kein Bild ausgewählt</span
            >
            <!--Adding the video file along with the image-->
            <p
              align="center"
              v-if="element.type === 'video' && element.url"
              :class="element.name"
            >
              <video controls width="400" height="200">
                <!-- poster="https://media.geeksforgeeks.org/wp-content/uploads/20200717172614/authPreLogo.png" -->
                <source :src="element.url" type="video/mp4" />
              </video>
            </p>
            <span
              v-else-if="element.type === 'video' && element.url === ''"
              class="required-url"
              :class="element.name"
              >Kein Video ausgewählt</span
            >

            <Cards
              v-if="element.type == 'objectlist'"
              :schema="element.schema"
              :itemWidth="'25%'"
              :cards="element.value"
              :viewOnly="isReadOnly"
              :class="element.name"
            />

            <!-- HR -->
            <hr v-if="element.type == 'horizontalrule'" :class="element.name" />

            <!-- Title -->
            <h3
              class="codelos-title"
              v-if="element.type == 'title' && !element.defaultTabs"
              :class="element.name"
            >
              {{ element.label }}
            </h3>
            <div
              class=""
              v-if="element.type == 'description'"
              :class="element.name"
            >
              {{ element.label }}
            </div>

            <label
              v-if="isTogglableListElement(element)"
              class="togglelist-label element-label"
              :class="element.name"
              @click="toggleTogglelist(element.id.toString())"
              :name="element.name"
              >{{ element.label }}</label
            >
            <div
              v-if="isTogglableListElement(element)"
              class="content"
              :id="element.id"
              v-show="opennedTogglelist.includes(element.id.toString())"
              :class="element.name"
            >
              <div
                class="form-group"
                v-for="(childElement, index) in element.elements"
                :key="childElement.name"
                v-show="
                  (childElement.type != 'html' &&
                    childElement.type != 'model') ||
                  isAdmin
                "
                :class="{
                  large: isLargeWrapperRequired(childElement.type),
                }"
              >
                <element-renderer
                  class="form-elements nested-form-elements"
                  :element="childElement"
                  :colIdx="colIdx"
                  :renderElement="renderElement"
                  :tabIdx="tabIdx"
                  :rowIdx="rowIdx"
                  :idx="`${childElement.name}${index}`"
                  :elementCount="element.elements.length"
                  :isLargeWrapperRequired="isLargeWrapperRequired"
                  :isUserHasAccess="isUserHasAccess"
                  :isEditable="isEditable"
                  :activeDropZone="activeDropZone"
                  :isContentEditable="isContentEditable"
                  :isAddOnly="isAddOnly"
                  :isEmptyFileInput="isEmptyFileInput"
                  :isFullWidth="isFullWidth"
                  @onFileSelected="onFileSelected"
                  @onFileDelete="onFileDelete"
                  :isAddingNewItem="isAddingNewItem"
                />
              </div>
            </div>

            <div
              v-if="
                !isChoiceWithCard(element) &&
                !isBackgroundImage &&
                !isShowDescription
              "
              class="radio-wrapper"
              :class="element.name"
            >
              <div
                v-for="option in element.options"
                :key="option.label"
                class="radioOption"
              >
                <input
                  :id="element.label + option.label"
                  :disabled="isReadOnly"
                  :type="element.type"
                  :value="option.label"
                  :checked="option.checked"
                  @change="changeChecked(element, option)"
                />
                <label :for="element.label + option.label">{{
                  option.label
                }}</label>
              </div>
            </div>
            <div
              v-if="isChoiceWithCard(element)"
              class="radio-wrapper image-with-detail-wrap"
              :class="{
                'defualt-view ': !isBackgroundImage && !isShowDescription,
                'both-image-description-active ':
                  isBackgroundImage && isShowDescription,
                'show-background': isBackgroundImage,
                'show-description': isShowDescription,
              }"
            >
              <div
                v-for="option in element.options"
                :key="option.label"
                class="image-with-detail-section"
                :style="[
                  isBackgroundImage
                    ? { 'background-image': `url(${option.img})` }
                    : '',
                ]"
              >
                <input
                  :id="element.label + option.label"
                  :disabled="!isContentEditable || isAddOnly"
                  :type="element.type"
                  :value="option.label"
                  :checked="option.checked"
                  @change="changeChecked(element, option)"
                />
                <label :for="element.label + option.label">
                  <div
                    class="option-image"
                    v-if="option.img && !isBackgroundImage"
                  >
                    <img :src="option.img" alt="" />
                  </div>
                  <div class="title">
                    <span>{{ option.label }}</span>
                  </div>
                  <div
                    class="detail"
                    v-if="isShowDescription && option.description"
                  >
                    {{ option.description }}
                  </div>
                </label>
              </div>
            </div>
            <select
              v-if="element.type == 'select'"
              :disabled="isReadOnly"
              :name="element.name"
              :id="element.name"
              :class="element.name"
            >
              <option v-for="option in element.options" :key="option.value">
                {{ option.label }}
              </option>
            </select>

            <!-- div wrapper -->

            <div
              v-if="isDivWrapperElement(element)"
              class="codelos-wrapper"
              :class="element.name.replace('-start', '')"
              :id="element.id"
            >
              <div
                class="form-group"
                v-for="(childElement, index) in element.elements"
                :key="childElement.name"
                v-show="
                  (childElement.type != 'html' &&
                    childElement.type != 'model') ||
                  isAdmin
                "
                :class="{
                  large: isLargeWrapperRequired(childElement.type),
                }"
              >
                <element-renderer
                  class="form-elements nested-form-elements"
                  :element="childElement"
                  :colIdx="colIdx"
                  :renderElement="renderElement"
                  :tabIdx="tabIdx"
                  :rowIdx="rowIdx"
                  :idx="`${childElement.name}${index}`"
                  :elementCount="element.elements.length"
                  :isLargeWrapperRequired="isLargeWrapperRequired"
                  :isUserHasAccess="isUserHasAccess"
                  :isEditable="isEditable"
                  :activeDropZone="activeDropZone"
                  :isContentEditable="isContentEditable"
                  :isEmptyFileInput="isEmptyFileInput"
                  :isFullWidth="isFullWidth"
                  @onFileSelected="onFileSelected"
                  @onFileDelete="onFileDelete"
                  :isAddingNewItem="isAddingNewItem"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import FilePicker from '../core/FilePicker.vue';
import GeneralDropdown from '../core/general-dropdown.vue';
import { getTableItems } from '../../services/userTable';
import NumberComponent from './NumberComponent.vue';

export default {
  name: 'element-renderer',
  data() {
    return {
      opennedTogglelist: [],
      databaseItemsList: [],
      isDbItemListLoading: false,
    };
  },
  props: {
    isEditable: {
      type: Boolean,
      required: true,
    },
    isContentEditable: {
      type: Boolean,
      required: true,
    },
    isUserHasAccess: {
      type: Boolean,
      required: true,
    },
    element: {
      type: Object,
      required: true,
    },
    tabIdx: {
      type: Number,
      required: true,
    },
    rowIdx: {
      type: Number,
      required: true,
    },
    colIdx: {
      type: Number,
      required: true,
    },
    idx: {
      type: [Number, String],
      required: true,
    },
    draggedElementIdx: {
      type: Number,
      default: null,
    },
    elementCount: {
      type: Number,
      required: true,
    },

    activeDropZone: {
      type: String,
    },
    isLargeWrapperRequired: {
      type: Function,
      required: true,
    },

    isEmptyFileInput: {
      type: Function,
      required: true,
    },
    isAddingNewItem: {
      type: Boolean,
      default: false,
    },
    isFullWidth: {
      type: Boolean,
      required: true,
    },
    selectedDatabase: {
      type: Object,
      default: () => null,
    },
    isAddOnly: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    generateDatabaseLink() {
      const domain = window.location.origin;
      const url = `${domain}/#/?section=data&database=${this.element.selectedDatabaseID}&item=${this.element.value}`;
      return url;
    },
    onDatabaseItemChange(dbItem) {
      this.element.value = dbItem.id;
    },
    async getDropdownOptions() {
      if (this.databaseItemsList.length === 0) {
        this.isDbItemListLoading = true;

        try {
          const listItems = await getTableItems(
            this.element.selectedDatabaseID
          );

          if (listItems && listItems.status === 200 && listItems.data) {
            if (this.element.selectedDatabaseType === 'loadprofile') {
              const filteredData = [...listItems.data].filter(
                (el) => el.id.includes('sheet') && el.ts_type === 'loadcurve'
              );
              this.databaseItemsList = filteredData;
            } else {
              this.databaseItemsList = listItems.data;
            }

            this.isDbItemListLoading = false;
          }
        } catch (error) {
          // eslint-disable-next-line
          console.log({ error });
          this.isDbItemListLoading = false;
        }
      }
    },
    isElementDragabble(element) {
      return this.isEditable && !element.isWizardTemplate;
    },
    renderElement(el, tabIdx, rowIdx, colIdx) {
      // Do not render frontend keys
      const excludeFromRendering = ['id', 'created', 'updated'];
      if (excludeFromRendering.includes(el.name)) return false;

      const e = el.structure,
        isTab = e.tab == tabIdx,
        isRow = e.row == rowIdx,
        isCol = e.col == colIdx;
      if ((isTab, isRow, isCol)) return true;
      else return false;
    },
    elementNeedsInput(element) {
      const withoutInput = [
        'number',
        'formatted_number',
        'range',
        'radio',
        'checkbox',
        'textarea',
        'html',
        'togglelist',
        'title',
        'description',
        'horizontalrule',
        'objectlist',
        'image',
        'video',
        'smartmessage',
        'filenew',
        'wrapper',
        'sheet',
        'linktodb',
      ];
      if (withoutInput.includes(element.type)) return false;
      return true;
    },
    elementNeedsLabel(element) {
      const withoutLabel = [
        'title',
        'horizontalrule',
        'description',
        'togglelist',
        'image',
        'video',
        'wrapper',
      ];
      if (
        withoutLabel.includes(element.type) ||
        (element.type == 'html' && !this.isEditable)
      )
        return false;
      return true;
    },
    hasCardAppearance(element) {
      if (
        element.attributes.hasOwnProperty('appearance') &&
        element.attributes.appearance == 'card'
      )
        return true;
    },
    hasOvalAppearance(element) {
      if (
        element.attributes.hasOwnProperty('appearance') &&
        element.attributes.appearance == 'oval'
      )
        return true;
    },
    isRequired(attrs) {
      if (Object.keys(attrs).includes('required'))
        return attrs.required == true;
      return false;
    },
    getPlaceholder(element) {
      if (Object.keys(element).includes('placeholder'))
        return element.placeholder;
      return null;
    },
    isChoiceWithCard(element) {
      let choiceTypes = ['radio', 'checkbox'];
      return (
        choiceTypes.includes(element.type) &&
        element.attributes.appearance === 'card'
      );
    },
    changeChecked(element, option) {
      if (element.type === 'radio') {
        element.options.forEach((e) => {
          e.checked = false;
        });
        option.checked = true;
        element.value = option.label;
      } else if (element.type === 'checkbox') {
        option.checked = !option.checked;
        if (option.checked) element.value.push(option.label);
        else element.value = element.value.filter((e) => e != option.label);
      }
    },
    isTogglableListElement(element) {
      if (this.isEditable) return false;
      else
        return element.type == 'togglelist' && element.name.includes('start');
    },
    isDivWrapperElement(element) {
      if (this.isEditable) return false;
      else return element.type == 'wrapper' && element.name.includes('start');
    },
    toggleTogglelist(id) {
      const index = this.opennedTogglelist.findIndex(
        (togglelistId) => togglelistId == id
      );
      if (index == -1) this.opennedTogglelist.push(id);
      else this.opennedTogglelist.splice(index, 1);
    },
    onFileSelected(file, element) {
      if (this.$refs[`filePickerRef${element.id}`]) {
        this.$refs[`filePickerRef${element.id}`].setFile(file);
      }
      this.$emit('onFileSelected', file, element);
    },
    onFileDelete(element, canEmit = true) {
      if (this.$refs[`filePickerRef${element.id}`]) {
        this.$refs[`filePickerRef${element.id}`].onDeleteFileButton(
          null,
          false
        );
      }
      if (canEmit) this.$emit('onFileDelete', element);
    },
    onFileSelectedFromElementProps(file) {
      this.$refs[`filePickerRef${this.element.id}`].setFile(file);
    },
    getStringifySheetObject(obj) {
      try {
        return !obj ? null : JSON.stringify(obj);
      } catch (err) {
        return null;
      }
    },
    showDescriptionAsList(description) {
      return description ? description.split(',') : [];
    },
    onNumberComponentInputChange(newValue) {
      this.element.value = newValue;
    },
    toggleErrorInForm(newValue) {
      this.$emit('toggleErrorInForm', newValue);
    },
  },
  computed: {
    ...mapGetters({
      isAdmin: 'getIsAdmin',
    }),
    isSheetDatabaseType() {
      return this.selectedDatabase && this.selectedDatabase.name === 'sheet';
    },
    isLinkViewOnly() {
      return (
        this.element.type === 'linktodb' && this.isReadOnly && !this.isEditable
      );
    },
    isReadOnly() {
      return !this.isContentEditable || this.isAddOnly;
    },
    isLinkinEdit() {
      return (
        this.element.type === 'linktodb' &&
        this.isEditable &&
        !this.isAddingNewItem &&
        !this.isReadOnly
      );
    },
    isLinkinAdd() {
      return (
        this.element.type === 'linktodb' &&
        !this.isEditable &&
        this.isAddingNewItem &&
        !this.isReadOnly
      );
    },
    isBackgroundImage() {
      return this.element.attributes.isImgShowInBackGround;
    },
    isShowDescription() {
      return this.element.attributes.isShowTitleAndDescription;
    },
  },

  components: {
    Cards: () => import('../widgets/cards.vue'),
    FilePicker,
    GeneralDropdown,
    NumberComponent,
  },
};
</script>

<style scoped>
.modal.task .form-group .tools {
  height: 100%;
  left: 2px;
  top: 6px;
  cursor: pointer;
  display: inline;
  width: 6%;
  opacity: 0;
  -webkit-transition: opacity 450ms ease-in-out;
  transition: opacity 450ms ease-in-out;
}

.modal.task .form-group:hover .tools {
  opacity: 1;
}

input,
select {
  font-family: 'Open Sans', open-sans, sans-serif;
  float: left;
  background: #fff;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  margin-right: 10px;
  margin-bottom: 15px;
  height: 51px;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-weight: 400;
  line-height: 19px;
  -webkit-font-smoothing: antialiased;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  font-size: 20px;
  font-weight: 300;
}

select {
  width: 100%;
}

textarea {
  resize: none;
  background: #fff;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 14px;
  font-weight: 400;
  line-height: 19px;
  padding: 12px;
  min-height: 140px;
}

input:disabled,
textarea:disabled,
select:disabled {
  border: none !important;
}

button {
  border: 1px solid #dee3e9;
  border-radius: 3px 3px 3px 3px;
  width: 30%;
  height: 51px;
  text-transform: uppercase;
  font-weight: 600;
  margin-top: 25px;
  cursor: pointer;
}

a {
  text-decoration: none;
  -webkit-transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  -o-transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  transition: color 0.2s ease, background-color 0.2s ease,
    border-color 0.2s ease;
  cursor: pointer;
}

a {
  background-color: transparent;
}
p {
  font-weight: 500;
  font-size: 12px;
  line-height: 21px;
  color: #7e8890;
  margin: 25px 0px 25px 0px;
}
.togglelist-label {
  font-weight: 500;
  font-size: 12px;
  color: #7e8890;
  margin: 12px 0px 12px 11px;
}

.wizard-title h3 {
  color: #5f697a;
  text-transform: capitalize;
  font-weight: 500;
  border-left: 2px solid #5f697a;
  padding-left: 10px;
  display: flex;
  align-items: center;
  font-size: 22px;
}

.form-input {
  cursor: text;
}

.form-input.highlighted {
  background: #ff000024;
  padding: 5px;
}

.form-elements .tools {
  float: right;
  width: 32px;
  height: 30px;
  margin-top: -41px;
}
.form-elements .tools i {
  font-size: 18px;
}
.form-elements .edit-overlay {
  top: 0;
  width: 94%;
  height: 100%;
  -webkit-transition: opacity 450ms ease-in-out;
  transition: opacity 450ms ease-in-out;
  cursor: unset;
  background: rgb(222 225 231 / 88%);
  opacity: 0;
  position: absolute;
  display: block;
  left: 0;
  border-radius: 6px;
  cursor: pointer;
}

.form-elements .edit-overlay:hover {
  opacity: 0.5;
}

.form-elements .drop-zone.horizontal {
  width: 100%;
}

.form-elements .drop-zone.vertical {
  float: right;
  position: relative;
}

.form-elements .element-label {
  margin-top: 12px;
}
.form-container label {
  width: 80%;
  font-family: 'Open Sans', open-sans, sans-serif;
  display: block;
  margin: 0px 10px 6px 12px;
  color: #464d5d;
  font-size: 14px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  font-weight: 300;
}

button {
  color: white;
  background: #4aae9b;
  border: 1px solid #4aae9b;
  border-radius: 2px;
  width: 30%;
  height: 51px;
  text-transform: uppercase;
  font-weight: 600;
  margin-top: 17px;
  margin-bottom: 20px;
  margin-right: 30px;
}
.form-elements input,
.form-elements select {
  border-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: medium none;
  padding-left: 12px;
  width: 95%;
  padding-bottom: 15px;
  margin-bottom: 0;
  height: 35px;
  padding-bottom: 14px;
  padding-top: 15px;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 13px;
  font-weight: 400;
  line-height: 19px;
  margin: 0 0 12px 11px;
  -webkit-font-smoothing: antialiased;
  -webkit-box-shadow: none;
  box-shadow: none;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
}

.radio-wrapper {
  width: 100%;
  display: block;
  margin-top: 4px;
}

.radioOption {
  display: inline-flex;
  height: auto;
  margin-right: 22px;
  align-items: center;
}

.radioOption label {
  line-height: 21px;
  padding: 0;
  margin-bottom: 0;
  margin-right: 0;
  width: auto;
  margin-left: 12px;
}

.radioOption input {
  float: none;
  width: 20px;
  margin-bottom: 0;
}

h3.codelos-title {
  /* color: #5f697a;
  font-size: 1.17em;
  font-weight: bold; */
  color: #5f697a;
  text-transform: capitalize;
  font-weight: 500;
  border-left: 2px solid #5f697a;
  padding-left: 10px;
  display: flex;
  align-items: center;
  font-size: 22px;
}
img.preview-image {
  width: 50%;
  margin: 10px auto;
  display: block;
}
.required-url {
  font-size: 14px;
  color: red;
  background: rgba(236, 237, 240, 0.88);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  margin-left: 11px;
  margin: 0;
  width: 100%;
}

.togglelist-label:hover {
  text-decoration: underline;
  cursor: pointer;
}

.edit-item-name ::v-deep .dropped-file-wrap .dropped-file-name {
  width: 100%;
  margin: 0;
}

.edit-item-name {
  width: 100%;
}

.edit-item-name ::v-deep .no-file-selected {
  width: 100%;
  margin: 0;
}
.update-select-file ::v-deep .file-selected {
  width: 100%;
}

.edit-item-name.update-select-file {
  margin-left: 0;
}
/* .content {
  background-color: #fff;
  width: 100%;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  text-align: center;
} */

/*New Formbuilder elements*/
.form-builder-elements,
.form-builder-elements * {
  box-sizing: border-box;
}

.form-builder-elements .form-builder-element-attributes .element-label {
  margin: 0 0 6px;
  width: 100%;
  font-weight: 300;
  font-family: 'Open Sans', open-sans, sans-serif;
  display: block;
  color: #464d5d;
  font-size: 14px;
}

.form-builder-elements .form-builder-element-attributes .form-input,
.form-builder-elements
  .form-builder-element-attributes
  .input-attribute
  ::v-deep
  .number-input {
  margin: 0 0 0;
  width: 100%;
  padding: 8px 12px;
  height: auto;
  color: #464d5d;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  font-size: 13px;
  font-weight: 400;
  line-height: 19px;
  box-shadow: none;
  background: transparent;
}

.form-builder-elements .form-builder-element-attributes input[type='range'] {
  padding: 0;
}

.form-builder-elements .form-builder-element-attributes .input-attribute {
  display: flex;
  flex-direction: column;
}
.form-builder-elements .form-builder-element-attributes {
  padding: 12px;
  margin-bottom: 0;
  border-radius: 6px;
  transition: all 300ms ease-in-out;
  position: relative;
}
.edit-mode.form-builder-elements .form-builder-element-attributes {
  cursor: pointer;
}
.form-builder-elements .form-builder-element-attributes:before {
  content: '';
  background: rgba(222, 225, 231, 0.88);
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  opacity: 0;
  visibility: hidden;
  transition: all 300ms ease-in-out;
}
.edit-mode.form-builder-elements .form-builder-element-attributes:hover:before {
  opacity: 0.5;
  visibility: visible;
}
.form-builder-element-wrap {
  position: relative;
  max-width: 85%;
  margin: 0 auto;
}

.edit-mode .edit-mode-tools {
  padding: 0;
  margin: 0;
  list-style: none;
  display: flex;
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 4px;
  opacity: 0;
  visibility: hidden;
  transition: all 300ms ease-in-out;
  z-index: 1;
}

.edit-mode .form-builder-element-wrap:hover .edit-mode-tools {
  opacity: 1;
  visibility: visible;
}

.edit-mode .edit-mode-tools i {
  font-size: 18px;
  margin: 0;
}

.edit-mode .edit-mode-tools li {
  cursor: pointer;
}

.start-form-builder .workBuilder-element-width.form-builder-element-wrap {
  max-width: 60%;
}
.nested-form-elements .form-builder-element-wrap {
  max-width: 100%;
}

.drop-zone.top {
  border-top: 5px solid #86c3f4;
}

.drop-zone.bottom {
  border-bottom: 5px solid #86c3f4;
}

.form-builder-element-wrap .link-to-db-wrapper-edit {
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 14px;
}

.form-builder-element-wrap .link-to-db-wrapper-edit a {
  text-decoration: underline;
  color: black;
}

.form-builder-element-wrap .link-to-db-wrapper-edit i {
  margin-right: 5px;
  font-size: 14px;
}

.form-builder-element-wrap .link-to-db-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
}

.form-builder-element-wrap .link-to-db-wrapper i {
  margin-right: 5px;
  font-size: 14px;
}

.form-builder-element-wrap .input-attribute .linktodb-edit-icon {
  cursor: pointer;
  font-size: 14px;
}

.form-builder-element-wrap .database-dropdown ::v-deep .customDropdown {
  width: 100% !important;
  border: 1px solid #dee3e9;
  border-radius: 2px;
  height: 37px;
  color: #464d5d;
  justify-content: space-between;
  padding: 0px 15px 0px 15px;
}

.form-builder-element-wrap ::v-deep .database-dropdown {
  width: 100%;
}
.form-builder-element-wrap ::v-deep .database-dropdown .dropdown-menu {
  width: 100%;
}

.form-builder-element-wrap
  .link-to-db-dropdown
  .database-dropdown
  ::v-deep
  .btn
  a {
  text-decoration: underline;
  color: black;
}

.form-builder-element-wrap
  .link-to-db-dropdown
  .database-dropdown
  ::v-deep
  .loading-overlay-wrapper {
  height: 40px;
}

.form-builder-element-wrap .link-to-db-dropdown .loading-overlay {
  position: absolute;
  background: transparent;
  width: 100%;
  z-index: 1;
  top: 0;
  text-align: center;
  height: 100%;
  border-radius: 4px;
}
.form-builder-element-wrap
  .link-to-db-dropdown
  .loading-overlay
  .progress-icon {
  content: ' ';
  display: block;
  width: 25px;
  height: 25px;
  margin: 1px;
  border-radius: 50%;
  border: 2px solid #1f2532c7;
  border-color: #1f2532c7 transparent #1f2532c7 transparent;
  animation: progress-icon 1.2s linear infinite;
  margin: 0 auto;
  top: 5px;
  position: absolute;
  left: 44%;
}

/*end*/

.image-section-radio {
  display: inline-block;
  width: 300px;
  height: 200px;
  margin-right: 20px;
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  border: 1px solid #eee;
  border-radius: 8px;
  box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.image-section-radio input {
  opacity: 0;
  position: absolute;
}
.image-section-radio input:checked + label {
  border-color: #000;
}
.image-section-radio label {
  margin: 0;
  display: block;
  width: 100%;
  height: 100%;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 15px;
}
.image-section-radio label .title {
  font-size: 18px;
  font-weight: 600;
  color: #000;
  position: relative;
  z-index: 1;
}
.image-with-detail-wrap.radio-wrapper {
  display: flex;
  flex-wrap: wrap;
}

.form-builder-elements .image-with-detail-wrap .image-with-detail-section {
  display: inline-block;
  margin-right: 10px;
  margin-bottom: 20px;
  border: 1px solid rgb(221, 221, 221);
  -webkit-box-shadow: 0 0 5px rgba(0, 0, 0, 0.1);
  box-shadow: none;
  border-radius: 5px;
  padding: 20px;
  min-height: 122px;
  min-width: 168px;
  background-position: center center;
  background-size: cover;
}

.form-builder-elements
  .show-background.image-with-detail-wrap
  .image-with-detail-section {
  min-width: 370px;
  min-height: 260px;
}
.form-builder-elements
  .show-description.image-with-detail-wrap
  .image-with-detail-section {
  width: 370px;
  min-height: 260px;
}
.form-builder-elements
  .show-background.image-with-detail-wrap
  .image-with-detail-section
  label
  .title {
  font-size: 30px;
  font-weight: 600;
}

.image-with-detail-wrap .image-with-detail-section input {
  opacity: 0;
  position: absolute;
  width: auto;
}
.image-with-detail-wrap .image-with-detail-section input:checked label {
  border-color: #000;
}

.image-with-detail-wrap .image-with-detail-section label {
  width: 100% !important;
  margin: 0 !important;
  padding: 0;
  border: 1px solid transparent;
  cursor: pointer;
}

.form-builder-elements
  .show-description.image-with-detail-wrap
  .image-with-detail-section
  .option-image {
  text-align: center;
}

.option-image img {
  max-width: 100%;
  width: 34px;
  margin-bottom: 39px;
}
.form-builder-elements
  .image-with-detail-wrap
  .image-with-detail-section
  .detail {
  margin-top: 39px;
}
.form-builder-elements
  .show-description.image-with-detail-wrap
  .option-image
  img {
  width: auto;
  height: 230px;
}

.form-builder-elements
  .image-with-detail-wrap
  .image-with-detail-section
  label
  .title {
  font-size: 15px;
  display: block;
  float: none;
  color: #5f697a;
  margin: 0;
  padding: 0 0;
  line-height: normal;
  text-transform: capitalize;
}
.form-builder-elements
  .show-description.image-with-detail-wrap
  .image-with-detail-section
  label
  .title {
  font-size: 30px;
  font-weight: 600;
  font-family: 'Open Sans', open-sans, sans-serif;
  color: #5f697a;
}
.form-builder-elements
  .image-with-detail-wrap.both-image-description-active
  .image-with-detail-section
  label {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}
.form-builder-elements .both-image-description-active .detail {
  padding: 10px;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 5px;
  word-wrap: break-word;
  margin-top: 60px !important;
}
</style>
